import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import { PromotoraEmpregadorDTO } from '../../shared/dto/promotoraEmpregadorDTO';
import { Button, Form } from 'react-bootstrap';
import CustomAlert from '../../shared/customAlert';
import PromotoraEmpregadorService from '../../services/promotoraEmpregadorService';
import OriginadorService from '../../services/originadorService';
import PromotoraService from '../../services/promotoraService'; 
import EmpregadorService from '../../services/empregadorService';
import EmpregadorOriginadorService from '../../services/empregadorOriginadorService';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { ProtectedAction } from '../../AccessControl';
import { OriginadorDTO } from '../../shared/dto/originadorDTO';
import { Originador } from '../../shared/model/originador';

const ROLE_PROMOTORA_UUID = "7ffffc56-5a2a-44d8-91cb-450af78ad5f0";
const ROLE_ORIGINADOR_UUID = "B6A1331C-A1DD-4972-AF84-9E62E04FF647";

type ItemLista = {
  id: string | number;
  nome: string;
  ativo: boolean;
  selected: boolean;
  originalRef: any;
};

function PromotoraEmpregador() {
  useVerificaPermissao();
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  
  // Estado inicial padrão
  const [tipoOrigem, setTipoOrigem] = useState<'PROMOTORA' | 'ORIGINADOR'>('PROMOTORA');
  const [modoOrdenacao, setModoOrdenacao] = useState<'codigo' | 'nome' | ''>('nome');

  const [promotorasDisp, setPromotorasDisp] = useState<ItemLista[]>([]);
  const [promotorasSel, setPromotorasSel] = useState<ItemLista[]>([]);
  const [originadoresDisp, setOriginadoresDisp] = useState<ItemLista[]>([]);
  const [originadoresSel, setOriginadoresSel] = useState<ItemLista[]>([]);

  const service = useMemo(() => new PromotoraEmpregadorService(), []);
  const originadorService = useMemo(() => new OriginadorService(), []);
  const promotoraService = useMemo(() => new PromotoraService(), []);
  const empregadorService = useMemo(() => new EmpregadorService(), []);
  const empregadorOriginadorService = useMemo(() => new EmpregadorOriginadorService(), []);

  const { chamada, idPromotora, idEmpregador } = location.state || {};
  const idReferencia = chamada === 'empregador' ? idEmpregador : idPromotora;

  useEffect(() => {
    const definirTipoInicial = async () => {
      // Se tiver ID de referência, tenta buscar configuração atual
      if (chamada === 'empregador' && idEmpregador) {
        try {
          const resEmp = await empregadorService.listarEmpregadorPorId(idEmpregador);
          if (resEmp.data?.tipoVinculo?.toUpperCase() === ROLE_ORIGINADOR_UUID.toUpperCase()) {
            setTipoOrigem('ORIGINADOR');
          } else {
            setTipoOrigem('PROMOTORA');
          }
        } catch (e) {
          console.error("Erro ao buscar tipo de vínculo inicial", e);
        }
      }
      // Após definir o tipo (ou manter o padrão), carrega as listas
      carregarListas(); 
    };
    definirTipoInicial();
  }, []); 

  const carregarListas = useCallback(async () => {
    setLoading(true);
    try {
      // Carrega Promotoras
      const resProm = await service.listarPromotorasEmpregadores(idPromotora, idEmpregador);
      const listaProm: PromotoraEmpregadorDTO[] = resProm.data;

      const promFormatadas: ItemLista[] = listaProm.map(p => ({
        id: p.id!,
        nome: p.nome!,
        ativo: p.ativo ?? false,
        selected: false,
        originalRef: p
      }));

      setPromotorasSel(promFormatadas.filter(p => p.ativo));
      setPromotorasDisp(promFormatadas.filter(p => !p.ativo));

      // Carrega Originadores
      const resOrigGeral = await originadorService.listarTodosOriginadoresAtivos();
      const originadoresGeral: OriginadorDTO[] = resOrigGeral.data;

      let idsVinculados: string[] = [];
      if (chamada === 'empregador' && idEmpregador) {
        try {
            // Busca vínculos de Originador existentes para este Empregador
            const resVinculados = await empregadorOriginadorService.listarEmpregadorOriginador(idEmpregador);
            if (resVinculados && resVinculados.data && Array.isArray(resVinculados.data)) {
                idsVinculados = resVinculados.data.map((v: OriginadorDTO) => String(v.id));
            }
        } catch (ignored) { }
      }

      const origFormatados: ItemLista[] = originadoresGeral.map(o => ({
        id: o.id!,
        nome: o.nome!,
        ativo: idsVinculados.includes(String(o.id)),
        selected: false,
        originalRef: o
      }));

      setOriginadoresSel(origFormatados.filter(o => o.ativo));
      setOriginadoresDisp(origFormatados.filter(o => !o.ativo));

    } catch (err) {
      setAlert({ message: 'Erro ao carregar listas.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [idPromotora, idEmpregador, service, originadorService, empregadorOriginadorService]);


  useEffect(() => {
    const filtrarPromotorasPorOriginador = async () => {
      if (tipoOrigem !== 'ORIGINADOR' || loading) return;
      
      if (originadoresSel.length === 0) {
          setPromotorasDisp([]);
          setPromotorasSel([]); 
          return;
      }
      
      try {
        const idsOriginadores = originadoresSel.map(o => Number(o.id));
        const res = await promotoraService.buscarPorOriginador(idsOriginadores);
        
        // Verifica quem já estava selecionado para não perder a seleção visual
        const idsJaSelecionados = promotorasSel.map(p => String(p.id));

        const novasPromotoras = res.data.map((p: any) => ({
          id: p.id,
          nome: p.nome,
          ativo: idsJaSelecionados.includes(String(p.id)),
          selected: false,
          originalRef: p
        }));

        setPromotorasSel(novasPromotoras.filter((p: ItemLista) => p.ativo));
        setPromotorasDisp(novasPromotoras.filter((p: ItemLista) => !p.ativo));
      } catch (e) {
        console.error(e);
      }
    };
    
    filtrarPromotorasPorOriginador();
  }, [originadoresSel, tipoOrigem]);

  const ordenarLista = useCallback((lista: ItemLista[]) => {
    return [...lista].sort((a, b) => {
      if (modoOrdenacao === 'nome') return a.nome.localeCompare(b.nome);
      if (modoOrdenacao === 'codigo') return Number(a.id) - Number(b.id);
      return 0;
    });
  }, [modoOrdenacao]);

  const RenderDualList = ({ titulo, disponiveis, setDisponiveis, selecionados, setSelecionados }: any) => {
    const mover = (isToSel: boolean, all: boolean) => {
      if (isToSel) {
        const paraMover = all ? disponiveis : disponiveis.filter((i: ItemLista) => i.selected);
        setSelecionados([...selecionados, ...paraMover.map((i: ItemLista) => ({...i, selected: false, ativo: true}))]);
        setDisponiveis(all ? [] : disponiveis.filter((i: ItemLista) => !i.selected));
      } else {
        const paraMover = all ? selecionados : selecionados.filter((i: ItemLista) => i.selected);
        setDisponiveis([...disponiveis, ...paraMover.map((i: ItemLista) => ({...i, selected: false, ativo: false}))]);
        setSelecionados(all ? [] : selecionados.filter((i: ItemLista) => !i.selected));
      }
    };

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>, setList: any, list: ItemLista[]) => {
      const ids = Array.from(e.target.selectedOptions).map((opt: HTMLOptionElement) => String(opt.value));
      setList(list.map(item => ({ ...item, selected: ids.includes(String(item.id)) })));
    };

    const dispOrd = useMemo(() => ordenarLista(disponiveis), [disponiveis, ordenarLista]);
    const selOrd = useMemo(() => ordenarLista(selecionados), [selecionados, ordenarLista]);

    return (
      <div className="card mb-4 shadow-sm border-0">
        <div className="card-header bg-white border-bottom-0 pt-3"><strong>{titulo}</strong></div>
        <div className="card-body pt-0">
          <div className="d-flex justify-content-between gap-3 align-items-center">
            <div className="flex-grow-1" style={{ width: '45%' }}>
              <select multiple size={10} className="form-control" onChange={e => handleSelect(e, setDisponiveis, disponiveis)} value={disponiveis.filter((i: ItemLista) => i.selected).map((i: ItemLista) => String(i.id))}>
                {dispOrd.map((i: ItemLista) => (<option key={i.id} value={i.id}>{modoOrdenacao === 'codigo' ? `${String(i.id).padStart(6, '0')} - ` : ''}{i.nome}</option>))}
              </select>
            </div>
            <div className="d-flex flex-column gap-2 mt-4">
              <Button variant="outline-primary" size="sm" onClick={() => mover(true, false)}>{">"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(true, true)}>{">>"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(false, false)}>{"<"}</Button>
              <Button variant="outline-primary" size="sm" onClick={() => mover(false, true)}>{"<<"}</Button>
            </div>
            <div className="flex-grow-1" style={{ width: '45%' }}>
              <select multiple size={10} className="form-control" onChange={e => handleSelect(e, setSelecionados, selecionados)} value={selecionados.filter((i: ItemLista) => i.selected).map((i: ItemLista) => String(i.id))}>
                {selOrd.map((i: ItemLista) => (<option key={i.id} value={i.id}>{modoOrdenacao === 'codigo' ? `${String(i.id).padStart(6, '0')} - ` : ''}{i.nome}</option>))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Validações
    if (tipoOrigem === 'ORIGINADOR') {
      if (originadoresSel.length === 0) {
        setAlert({ message: `Selecione ao menos uma ${Originador.DESCRITIVO_GERAL}.`, type: 'warning' });
        return;
      }
      if (promotorasSel.length === 0) {
        setAlert({ message: 'Selecione ao menos uma Promotora vinculada.', type: 'warning' });
        return;
      }
    } else {
      if (promotorasSel.length === 0) {
        setAlert({ message: 'Selecione ao menos uma Promotora.', type: 'warning' });
        return;
      }
    }

    setLoading(true);
    try {
      const uuidVinculo = tipoOrigem === 'ORIGINADOR' ? ROLE_ORIGINADOR_UUID : ROLE_PROMOTORA_UUID;

      const payloadPromotora = [
        ...promotorasDisp.map(p => ({ ...p.originalRef, ativo: false })),
        ...promotorasSel.map(p => ({ ...p.originalRef, ativo: true })),
      ];
      
      await service.incluirAlterarPromotoraEmpregador(chamada, idReferencia, payloadPromotora, uuidVinculo);

      if (tipoOrigem === 'ORIGINADOR') {
        const payloadOriginador = [
           ...originadoresDisp.map(o => ({ ...o.originalRef, ativo: false })),
           ...originadoresSel.map(o => ({ ...o.originalRef, ativo: true }))
        ];
        
        await empregadorOriginadorService.incluirAlterarEmpregadorOriginador(idReferencia, payloadOriginador);
      }

      setAlert({ message: 'Dados salvos com sucesso!', type: 'success' });
      await carregarListas();

    } catch (err) {
      console.error(err);
      setAlert({ message: 'Erro ao salvar os dados!', type: 'error' });
    } finally { 
      setLoading(false); 
    }
  };

  const handleTrocaTipo = (tipo: 'PROMOTORA' | 'ORIGINADOR') => {
      setTipoOrigem(tipo);
      
      if(tipo === 'PROMOTORA') {
          // Volta para Promotora: recarrega a lista original
          carregarListas();
      } else {
          // Vai para Originador: limpa promotoras se não houver originador selecionado
          if (originadoresSel.length === 0) {
            setPromotorasDisp([]);
            setPromotorasSel([]);
          }
      }
  };

  const showPromotoraCard = tipoOrigem === 'PROMOTORA' || (tipoOrigem === 'ORIGINADOR' && originadoresSel.length > 0);

  return (
    <div className="position-relative m-md-3">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      {loading ? <Spinner loading={loading} /> : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5>{chamada === 'empregador' ? 'Cadastro > Empregador > Origens' : 'Cadastro > Promotora > Vínculos'}</h5>
            <Button id="App-button-cor-principal" onClick={() => window.history.back()}>Voltar</Button>
          </div>

          <div className="card p-3 mb-3 bg-white border-0 shadow-sm">
            <label className="small fw-bold">Modo de Exibição das Listas</label>
            <select className="form-control mt-1" value={modoOrdenacao} onChange={(e) => setModoOrdenacao(e.target.value as any)}>
              <option value="nome">Ordem Alfabética (Nome)</option>
              <option value="codigo">Ordem de Código (ID)</option>
            </select>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="card p-3 mb-4 bg-light border-0">
              <h6 className="mb-3">Tipo de Vínculo (Esteira):</h6>
              <div className="d-flex gap-4">
                <Form.Check 
                  type="radio" label="Promotora" name="vinculo" id="radioProm"
                  checked={tipoOrigem === 'PROMOTORA'} onChange={() => handleTrocaTipo('PROMOTORA')} 
                />
                <Form.Check 
                  type="radio" label="Originador" name="vinculo" id="radioOrig"
                  checked={tipoOrigem === 'ORIGINADOR'} onChange={() => handleTrocaTipo('ORIGINADOR')} 
                />
              </div>
            </div>

            {tipoOrigem === 'ORIGINADOR' && (
              <RenderDualList 
                titulo="1. Seleção de Originadores" disponiveis={originadoresDisp} 
                setDisponiveis={setOriginadoresDisp} selecionados={originadoresSel} setSelecionados={setOriginadoresSel} 
              />
            )}

            {showPromotoraCard && (
              <RenderDualList 
                titulo={tipoOrigem === 'ORIGINADOR' ? "2. Promotoras Filtradas (Vinculadas aos Originadores)" : "Empregadores do Cadastro"} 
                disponiveis={promotorasDisp} setDisponiveis={setPromotorasDisp} 
                selecionados={promotorasSel} setSelecionados={setPromotorasSel} 
              />
            )}

            <div className="mt-4 pb-5">
              <ProtectedAction>
                <Button type="submit" variant="success" size="lg">Salvar Configurações</Button>
              </ProtectedAction>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default PromotoraEmpregador;